import { ReactNode, useEffect } from "react";
import { Trans, useTranslation } from "react-i18next";
import { FiChevronLeft, FiChevronRight } from "react-icons/fi";
import {
  TableWrapper,
  Table,
  TableHeader,
  TableHeaderCell,
  TableBody,
  TableRow,
  TableCell,
  PaginationWrapper,
  PaginationInfo,
  PaginationControls,
  RowsPerPage,
  PaginationButtons,
  PaginationEllipsis,
} from "./Styles";

export interface Column<T> {
  key: string;
  label: string;
  width?: string;
  sortable?: boolean;
  align?: "left" | "center" | "right";
  render: (item: T) => ReactNode;
}

export interface DataTableProps<T> {
  columns: Column<T>[];
  data: T[];
  onRowClick?: (item: T) => void;
  sortColumn?: string;
  sortDirection?: "asc" | "desc";
  onSort?: (column: string) => void;
  currentPage: number;
  rowsPerPage: number;
  totalItems: number;
  onPageChange: (page: number) => void;
  onRowsPerPageChange: (rows: number) => void;
  emptyMessage?: string;
  getRowKey: (item: T) => string;
}

export function DataTable<T>({
  columns,
  data,
  onRowClick,
  sortColumn,
  sortDirection = "asc",
  onSort,
  currentPage,
  rowsPerPage,
  totalItems,
  onPageChange,
  onRowsPerPageChange,
  emptyMessage,
  getRowKey,
}: DataTableProps<T>) {
  const { t } = useTranslation();
  const resolvedEmptyMessage = emptyMessage ?? t("common.dataTable.emptyMessage");

  const getSortIcon = (columnKey: string) => {
    if (sortColumn !== columnKey) {
      return (
        <span className="sort-icon" style={{ fontSize: '1rem', opacity: 0.4 }}>
          ↕
        </span>
      );
    }
    return (
      <span className="sort-icon" style={{ fontSize: '1rem', opacity: 1 }}>
        {sortDirection === "asc" ? "↑" : "↓"}
      </span>
    );
  };

  const totalPages = Math.max(1, Math.ceil(totalItems / rowsPerPage));

  // When data shrinks (e.g., items deleted) while on a higher page, the current page may
  // become invalid. We auto-correct to the last valid page to avoid showing nonsensical
  // pagination like "Showing 11 to 5 of 5 results".
  useEffect(() => {
    if (totalItems > 0 && currentPage > totalPages) {
      onPageChange(totalPages);
    }
  }, [totalItems, currentPage, totalPages, onPageChange]);

  // Use a safe page value for display calculations in case the effect hasn't fired yet
  const safePage = Math.min(Math.max(1, currentPage), totalPages);
  const startIndex = (safePage - 1) * rowsPerPage;
  const endIndex = Math.min(startIndex + rowsPerPage, totalItems);

  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      onPageChange(page);
    }
  };

  const getPageNumbers = (): (number | "ellipsis")[] => {
    if (totalPages <= 7) {
      // If 7 or fewer pages, show all pages
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }

    const pages: (number | "ellipsis")[] = [];
    const showAroundCurrent = 1; // Number of pages to show on each side of current page

    // Always show first page
    pages.push(1);

    // Calculate the range around current page (use safePage for consistent display)
    const startPage = Math.max(2, safePage - showAroundCurrent);
    const endPage = Math.min(totalPages - 1, safePage + showAroundCurrent);

    // Add ellipsis after first page if there's a gap
    if (startPage > 2) {
      pages.push("ellipsis");
    }

    // Add pages around current page
    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }

    // Add ellipsis before last page if there's a gap
    if (endPage < totalPages - 1) {
      pages.push("ellipsis");
    }

    // Always show last page (if more than 1 page)
    if (totalPages > 1) {
      pages.push(totalPages);
    }

    return pages;
  };

  return (
    <>
      <TableWrapper>
        <Table>
          <TableHeader>
            <tr>
              {columns.map((column) => (
                <TableHeaderCell
                  key={column.key}
                  className={column.sortable ? "sortable" : ""}
                  onClick={() => column.sortable && onSort?.(column.key)}
                  style={{
                    width: column.width,
                    textAlign: column.align || "left",
                    cursor: column.sortable ? "pointer" : "default",
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: "0.25rem" }}>
                    {column.label}
                    {column.sortable && getSortIcon(column.key)}
                  </div>
                </TableHeaderCell>
              ))}
            </tr>
          </TableHeader>
          <TableBody>
            {data.length === 0 ? (
              <TableRow className="empty-row">
                <TableCell colSpan={columns.length}>{resolvedEmptyMessage}</TableCell>
              </TableRow>
            ) : (
              data.map((item) => (
                <TableRow
                  key={getRowKey(item)}
                  onClick={() => onRowClick?.(item)}
                  style={{ cursor: onRowClick ? "pointer" : "default" }}
                >
                  {columns.map((column) => (
                    <TableCell
                      key={column.key}
                      style={{ textAlign: column.align || "left" }}
                    >
                      {column.render(item)}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableWrapper>

      {totalItems > 0 && (
        <PaginationWrapper>
          <PaginationInfo>
            <Trans
              i18nKey="common.dataTable.paginationInfo"
              values={{ start: startIndex + 1, end: endIndex, total: totalItems }}
              components={{
                1: <span className="number" />,
                2: <span className="number" />,
                3: <span className="number" />,
              }}
            />
          </PaginationInfo>

          <PaginationControls>
            <RowsPerPage>
              <span>{t("common.dataTable.rowsPerPageLabel")}</span>
              <select
                value={rowsPerPage}
                onChange={(e) => onRowsPerPageChange(Number(e.target.value))}
              >
                <option value={5}>5</option>
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
              </select>
            </RowsPerPage>

            <PaginationButtons>
              <button
                disabled={safePage === 1}
                onClick={() => handlePageChange(safePage - 1)}
              >
                <FiChevronLeft />
              </button>
              {getPageNumbers().map((page, index) => {
                if (page === "ellipsis") {
                  return <PaginationEllipsis key={`ellipsis-${index}`}>...</PaginationEllipsis>;
                }
                return (
                  <button
                    key={page}
                    className={safePage === page ? "active" : ""}
                    onClick={() => handlePageChange(page)}
                  >
                    {page}
                  </button>
                );
              })}
              <button
                disabled={safePage === totalPages}
                onClick={() => handlePageChange(safePage + 1)}
              >
                <FiChevronRight />
              </button>
            </PaginationButtons>
          </PaginationControls>
        </PaginationWrapper>
      )}
    </>
  );
}





